/**
 * `/v1/performances` — every endpoint the line-up page consumes.
 *
 * Nothing here declares a DTO of its own: `@tvitvi/shared` is the contract and this module only
 * knows the paths and the cache tier. Every response is parsed with the very schema the API
 * serialised it with, so a contract change is a typecheck failure rather than a runtime surprise.
 * The query keys live in `apps/web/src/api/query-keys.ts` with every other key, which is where
 * CLAUDE.md says a cross-cutting concern belongs.
 *
 * ---------------------------------------------------------------------------------------------
 * FOUR CALLS, AND NO `…/status` FIFTH.
 *
 * Decision 0011 is explicit that moving the booking chain IS an update: "Hvem som får flytte
 * statusen avgjøres av `performance:update` i matrisen, som alle andre skriverettigheter", and
 * the contract adds that there is deliberately no `performance:set_status` cell because "a second
 * rule saying the same thing is a rule that eventually disagrees with the first". So a chain step
 * is `PATCH /v1/performances/:id` with one field, exactly like renaming it — which is why
 * `useSetPerformanceStatus` below is the SAME request as `useUpdatePerformance` and differs only
 * in being optimistic. That is the opposite of Tasks, where `POST /v1/tasks/:id/status` exists
 * because completing a task is `task:complete` and editing it is `task:update`.
 *
 * WHY THE STATUS MOVE IS OPTIMISTIC AND THE FORM SAVE IS NOT. DESIGN.md §7 names it —
 * "Optimistisk UI på enkle handlinger: haker av oppgave, endrer status" — and it is safe here for
 * the same reason it is safe on a task: the change is reversible in both directions, the snapshot
 * goes back on failure, and a wrong status is one more click to correct rather than something
 * lost. A form save moves up to six fields at once and has a dialog to report into, so it waits
 * for the answer.
 *
 * NO CALENDAR INVALIDATION. `qk.calendar(orgId)` counts tasks per day and nothing else today;
 * `contracts/calendar.ts` says the response is on its way to carrying performances, and the day
 * it does, this is the line that has to grow. Invalidating a key over data it does not yet hold
 * would be a round trip that proves nothing.
 * ---------------------------------------------------------------------------------------------
 */
import {
  MAX_PAGE_SIZE,
  PERFORMANCE_SORT_KEYS,
  performanceDtoSchema,
  performanceListResponseSchema,
  reorderPerformancesResponseSchema,
  type CreatePerformanceRequest,
  type PerformanceDto,
  type PerformanceListResponse,
  type PerformanceSortKey,
  type PerformanceStatus,
  type ReorderPerformancesRequest,
  type ReorderPerformancesResponse,
  type UpdatePerformanceRequest,
} from '@tvitvi/shared';
import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk, type PerformanceListFilters } from '@/api/query-keys';

import { DEFAULT_DIRECTION, DEFAULT_SORT } from './search-params';

const PATH = '/performances';

/** `?status=hold,signed` — the comma form `listPerformancesQuerySchema` accepts, so one parameter. */
function statusParam(statuses: readonly PerformanceStatus[] | undefined): string | undefined {
  return statuses && statuses.length > 0 ? statuses.join(',') : undefined;
}

/**
 * One årgang's line-up, ordered by the server.
 *
 * ONE PAGE, AND THE PAGE SAYS SO IF IT IS NOT THE WHOLE ANSWER. `MAX_PAGE_SIZE` is 100, which is
 * a large festival's whole line-up several times over, so this asks for one page rather than
 * growing a "load more" nobody will press — the call `editionsQueryOptions` and `tasksQueryOptions`
 * both made. It does NOT quietly drop the rest: the page renders a line when `hasMore` comes back
 * true, which is the one thing a list must never leave unsaid.
 *
 * NO CURSOR TRAVELS WITH A SORT CHANGE. A cursor records the sort spec it was minted for and
 * `decodeCursor` refuses it under any other, so a stale one is a 400 rather than a wrong page.
 * There is nothing to drop here: the sort is part of `queryKey`, so a different order is a
 * different query with its own first page, and `filters` carries no position at all.
 *
 * `editionId` IS ALWAYS SENT even though the API does not require it — the contract says so in as
 * many words: "The line-up of one year. The page always sends it; the API does not require it."
 * A request without it would answer with every performance in the organisation under one årgang's
 * heading.
 */
export function performancesQueryOptions(
  orgId: string,
  editionId: string,
  filters: PerformanceListFilters,
) {
  return queryOptions({
    queryKey: qk.performanceList(orgId, editionId, filters),
    queryFn: ({ signal }): Promise<PerformanceListResponse> =>
      apiRequest({
        path: PATH,
        // Spread rather than assigned: a key present with `undefined` is dropped by `buildUrl`
        // but is still a key, and "the request carries no stage filter" is a property this page's
        // test has to be able to assert on the request itself.
        query: {
          editionId,
          sort: filters.sort ?? DEFAULT_SORT,
          direction: filters.direction ?? DEFAULT_DIRECTION,
          limit: MAX_PAGE_SIZE,
          ...(filters.stageId !== undefined ? { stageId: filters.stageId } : {}),
          ...(statusParam(filters.statuses) !== undefined
            ? { status: statusParam(filters.statuses) }
            : {}),
          ...(filters.search !== undefined ? { search: filters.search } : {}),
        },
        schema: performanceListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * Every booking of one artist, across every year and every festival (sak 49).
 *
 * WHY IT IS NOT `performancesQueryOptions` WITH A FILTER: that one takes an `editionId` as a
 * required argument, because a line-up is a year's. This question has no year in it — "hvor har vi
 * hatt denne artisten" is more useful the further back it goes — so a shared function would need an
 * optional edition and a cache key that could be missing its own discriminator.
 *
 * `sort=time` and DESCENDING: the useful end of an artist's history is the recent end. The oldest
 * booking is a fact about the festival; the newest is what a booker is about to repeat or avoid.
 */
export function artistBookingsQueryOptions(orgId: string, contactId: string) {
  return queryOptions({
    queryKey: qk.artistBookings(orgId, contactId),
    queryFn: ({ signal }): Promise<PerformanceListResponse> =>
      apiRequest({
        path: PATH,
        query: {
          artistContactId: contactId,
          sort: 'time',
          direction: 'desc',
          limit: MAX_PAGE_SIZE,
        },
        schema: performanceListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * One booking, on its own page — `GET /v1/performances/:id`.
 *
 * THE SAME DTO THE LIST RETURNS, which is what makes the detail page cheap: `contracts/
 * performances.ts` declares exactly one `PerformanceDto`, notes included, and the artist and the
 * stage are "resolved in the same statement as the row". So this is not a richer read — it is the
 * same row addressed by id, for a reader who arrived by link rather than through the line-up.
 *
 * DETAIL TIER, like every other single-object read. A booking's status is exactly the kind of
 * thing a colleague moves while you have the page open.
 *
 * IT IS UNDER `qk.performances(orgId)`, so the PATCH the chain makes on this very page — which
 * invalidates that prefix — refreshes this entry without `useUpdatePerformance` learning a second
 * key. `qk.task` and `qk.contact` sit beside their own lists for the same reason.
 */
export function performanceQueryOptions(orgId: string, performanceId: string) {
  return queryOptions({
    queryKey: qk.performance(orgId, performanceId),
    queryFn: ({ signal }): Promise<PerformanceDto> =>
      apiRequest({ path: `${PATH}/${performanceId}`, schema: performanceDtoSchema, signal }),
    ...CACHE.detail,
  });
}

/**
 * QA13k F14: skjemaet viser feilen selv (en Callout med grunnen). Uten dette la den felles
 * feilbehandleren en toast oppå — «Noen andre endret dette samtidig» for en låst artist.
 */
export interface PerformanceMutationOptions {
  handledLocally?: boolean;
}

export function useCreatePerformance(
  orgId: string,
  options: PerformanceMutationOptions = {},
): UseMutationResult<PerformanceDto, unknown, CreatePerformanceRequest> {
  return useMutation({
    mutationFn: (input: CreatePerformanceRequest) =>
      apiRequest({ path: PATH, method: 'POST', body: input, schema: performanceDtoSchema }),
    meta: {
      invalidates: [qk.performances(orgId)],
      ...(options.handledLocally === true ? { errorHandledLocally: true as const } : {}),
    },
  });
}

export interface UpdatePerformanceInput {
  performanceId: string;
  body: UpdatePerformanceRequest;
}

export function useUpdatePerformance(
  orgId: string,
  options: PerformanceMutationOptions = {},
): UseMutationResult<PerformanceDto, unknown, UpdatePerformanceInput> {
  return useMutation({
    mutationFn: ({ performanceId, body }: UpdatePerformanceInput) =>
      apiRequest({
        path: `${PATH}/${performanceId}`,
        method: 'PATCH',
        body,
        schema: performanceDtoSchema,
      }),
    meta: {
      /* Dato, tid og scene kan avvike fra en signert kontrakt (274.2 a): varselet må tegnes på nytt. */
      invalidates: [qk.performances(orgId), qk.contracts(orgId)],
      ...(options.handledLocally === true ? { errorHandledLocally: true as const } : {}),
    },
  });
}

export interface SetPerformanceStatusInput {
  performanceId: string;
  status: PerformanceStatus;
}

/**
 * A move along the booking chain — the same `PATCH` as any other edit, drawn optimistically.
 *
 * DECISION 0011 IS WHY THIS TAKES NO "FROM" AND CHECKS NO TRANSITION. "Det trenger ikke være låst
 * i rekkefølge. Vi må kunne f.eks hoppe rett inn i steg 4." There is no legal-next-step list to
 * consult, in the API or here; a client that kept one would be the transition table the decision
 * refused, rebuilt in the browser where nothing enforces it.
 *
 * What is NOT optimistic is anything the server owns downstream of the status. The cached row
 * shows the new status and waits for the real answer for everything else, rather than inventing
 * values that would be a few hundred milliseconds wrong.
 */
export function useSetPerformanceStatus(
  orgId: string,
): UseMutationResult<
  PerformanceDto,
  unknown,
  SetPerformanceStatusInput,
  { entries: [readonly unknown[], unknown][] }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ performanceId, status }: SetPerformanceStatusInput) =>
      apiRequest({
        path: `${PATH}/${performanceId}`,
        method: 'PATCH',
        body: { status },
        schema: performanceDtoSchema,
      }),
    onMutate: async ({ performanceId, status }) => {
      await queryClient.cancelQueries({ queryKey: qk.performances(orgId) });
      const entries = queryClient.getQueriesData({ queryKey: qk.performances(orgId) });

      for (const [key, value] of entries) {
        const list = value as PerformanceListResponse | undefined;
        if (list && Array.isArray(list.items)) {
          queryClient.setQueryData(key, {
            ...list,
            items: list.items.map((row) => (row.id === performanceId ? { ...row, status } : row)),
          });
          continue;
        }

        /*
         * AND THE DETAIL PAGE, WHICH HOLDS ONE ROW RATHER THAN A PAGE OF THEM.
         *
         * `qk.performance` is under the same prefix as the lists — deliberately, so this
         * mutation's invalidation reaches it — and the chain is drawn on that page too. Without
         * this branch the very control that was pressed would be the one control on screen that
         * did not move until the refetch landed, which reads as the press having missed.
         */
        const row = value as PerformanceDto | undefined;
        if (row && typeof row.id === 'string' && row.id === performanceId) {
          queryClient.setQueryData(key, { ...row, status });
        }
      }
      return { entries: entries as [readonly unknown[], unknown][] };
    },
    onError: (_error, _input, context) => {
      for (const [key, value] of context?.entries ?? []) queryClient.setQueryData(key, value);
    },
    /*
     * OG ØKONOMI, FORDI SIGNERINGEN NÅ SKRIVER EN KOSTNADSLINJE (vedtak 85).
     *
     * Å flytte en booking til «signert» fører honoraret som forbruk på den bookingen. Uten dette
     * sveipet ville Økonomi-fanen — og kontraktfanens eget reskontro-kort — vist summen fra før
     * kronene ble ført, på nettopp den handlingen som førte dem. Den optimistiske tegningen over
     * rører fortsatt bare statusen: det som er serverens nedstrøms, ventes det på.
     */
    meta: {
      invalidates: [qk.performances(orgId), qk.economy(orgId), qk.performanceContract(orgId)],
    },
  });
}

/**
 * The running order, rewritten in one request — `POST /v1/performances/reorder`.
 *
 * THE WHOLE ORDER TRAVELS, AND THE CONTRACT ARGUES IT AT LENGTH: sending only the moved row would
 * need a `sortOrder` BETWEEN its new neighbours, gaps run out, and the fix for that is a renumber,
 * "so it may as well be the only path and not the fallback nobody exercises". `reorder.ts` carries
 * the other half of the rule — when this page is allowed to claim it holds the complete order.
 *
 * OPTIMISTIC, FOR THE SAME REASON `useSetPerformanceStatus` IS. DESIGN.md §7 names the class of
 * action — "optimistisk UI på enkle handlinger" — and a drag is the strongest case in it: the row
 * is under the pointer, and a list that snapped back for 300 ms while the server answered would
 * read as the drag having failed. Every cached page of this årgang is reordered from the ids in
 * the request, and the snapshot goes back untouched on failure.
 *
 * WHY EVERY CACHED ENTRY AND NOT JUST THE ONE ON SCREEN. `qk.performanceList` keys by filters, so
 * the same årgang can be in the cache several times over — the unfiltered order, and whatever the
 * reader narrowed a minute ago. They all carry `sortOrder`, and an entry left holding the old
 * numbers is what the reader would be shown the instant they cleared a filter.
 *
 * AND WHY ONLY SOME OF THEM ARE RE-SEQUENCED. `sortOrder` is corrected everywhere — it is a field
 * of the row and the server has just changed it — but the ITEMS are only reordered in an entry
 * that is drawn in running order. A cached page keyed `sort: 'time'` holds its rows in time order,
 * and putting them into running order would be this optimistic update inventing a list the server
 * never sent: the reader would switch to "Tid" and see a running order under a time heading until
 * the refetch corrected it. The sort is read off the key, which is the only place that knows.
 */
export interface ReorderPerformancesInput {
  editionId: string;
  /** Every performance of the edition, in the order they now appear. Position becomes sortOrder. */
  performanceIds: readonly string[];
}

export function useReorderPerformances(
  orgId: string,
): UseMutationResult<
  ReorderPerformancesResponse,
  unknown,
  ReorderPerformancesInput,
  { entries: [readonly unknown[], unknown][] }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ editionId, performanceIds }: ReorderPerformancesInput) =>
      apiRequest({
        path: `${PATH}/reorder`,
        method: 'POST',
        // Copied rather than passed through: the request type is the contract's own inferred
        // shape and that is a mutable `string[]`, while everything upstream of here holds the
        // order as `readonly` on purpose. The copy is the boundary between the two.
        body: {
          editionId,
          performanceIds: [...performanceIds],
        } satisfies ReorderPerformancesRequest,
        schema: reorderPerformancesResponseSchema,
      }),
    onMutate: async ({ performanceIds }) => {
      await queryClient.cancelQueries({ queryKey: qk.performances(orgId) });
      const entries = queryClient.getQueriesData({ queryKey: qk.performances(orgId) });

      // The position each id now claims. Built once rather than per row: a line-up is tens of
      // rows and there are several cached pages of it, and an indexOf per row per page is the
      // quadratic version of the same answer.
      const position = new Map(performanceIds.map((id, index) => [id, index]));

      for (const [key, value] of entries) {
        const list = value as PerformanceListResponse | undefined;
        if (!list || !Array.isArray(list.items)) continue;

        // The number the server is about to write, applied wherever the row is cached. A row the
        // request never named is left exactly as it was rather than given a made-up position.
        const renumbered = list.items.map((row) => {
          const at = position.get(row.id);
          return at === undefined ? row : { ...row, sortOrder: at };
        });

        const items =
          sortOfKey(key) === 'order'
            ? renumbered
                .map((row, index) => ({
                  row,
                  at: position.get(row.id) ?? Number.MAX_SAFE_INTEGER,
                  index,
                }))
                // `index` breaks the tie so a row the request never named keeps its place
                // relative to its neighbours rather than being shuffled by an unstable sort.
                .sort((left, right) => left.at - right.at || left.index - right.index)
                .map(({ row }) => row)
            : renumbered;

        queryClient.setQueryData(key, { ...list, items });
      }
      return { entries: entries as [readonly unknown[], unknown][] };
    },
    onError: (_error, _input, context) => {
      for (const [key, value] of context?.entries ?? []) queryClient.setQueryData(key, value);
    },
    meta: { invalidates: [qk.performances(orgId)] },
  });
}

/**
 * Which order a cached list entry is DRAWN in, read off its own query key.
 *
 * `qk.performanceList` puts the filter object last, and the sort is one of its fields — so the key
 * is the only thing that knows whether the rows behind it are in running order, time order or
 * status order. Anything unrecognisable falls back to `DEFAULT_SORT`, which is what an entry
 * written before a sort was ever chosen actually holds.
 */
function sortOfKey(key: readonly unknown[]): PerformanceSortKey {
  const filters = key.at(-1);
  if (typeof filters !== 'object' || filters === null) return DEFAULT_SORT;
  const sort = (filters as { sort?: unknown }).sort;
  if (typeof sort !== 'string') return DEFAULT_SORT;
  return (PERFORMANCE_SORT_KEYS as readonly string[]).includes(sort)
    ? (sort as PerformanceSortKey)
    : DEFAULT_SORT;
}

export function useDeletePerformance(orgId: string): UseMutationResult<void, unknown, string> {
  return useMutation({
    mutationFn: (performanceId: string) =>
      apiRequest({ path: `${PATH}/${performanceId}`, method: 'DELETE' }),
    meta: { invalidates: [qk.performances(orgId)] },
  });
}

/** «Angre» på en slettet programpost (239.49): samme rad og samme id tilbake. */
export function useRestorePerformance(
  orgId: string,
): UseMutationResult<PerformanceDto, unknown, string> {
  return useMutation({
    mutationFn: (performanceId: string) =>
      apiRequest({
        path: `${PATH}/${performanceId}/restore`,
        method: 'POST',
        schema: performanceDtoSchema,
      }),
    meta: { invalidates: [qk.performances(orgId)] },
  });
}
